// src/components/checklists/ChecklistFiller.js
// chklstweb-3a7f1c58 — the inline questions for ONE checklist on a job (web).
//
// AUTOSAVE. Every answer goes through saveChecklistAnswers, which merges only the
// keys sent (dotted paths), so two people answering different questions never
// overwrite each other. Taps save at once; typed fields save 800 ms after the last
// keystroke and at once on blur. Saves run ONE AT A TIME per checklist, so an older
// save can never land after a newer one.
//
// DRAFTS. A typed value is kept locally while its field has focus or its save is in
// flight, so the live snapshot can't wipe what someone is typing. Once saved and
// blurred, the server's value shows again.
//
// Answer formats are the server's (functions/utils/checklists.js validateAnswer):
// yesno 'yes'|'no'|'na', checkbox boolean, number a number, date 'YYYY-MM-DD',
// photos { noteId }, signature { noteId, signerName }, multi a list of options in the checklist's
// order (chklstmulti-3e9b7c15). null clears an answer. A note (allowNote) is sent as
// saveChecklistAnswers { notes } on the same one-at-a-time queue; it never counts as an answer.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { saveChecklistAnswers, isChecklistAnswered, ANSWER_NOTE_MAX, takesAttachedPhotos } from '../../utils/checklistClient';
import ChecklistMediaQuestion from './ChecklistMediaQuestion';

const TEXT_DEBOUNCE_MS = 800;
const NOTE_PREFIX = 'note:'; // chklstmulti-3e9b7c15 — a note's draft key; question ids can't contain ':'
const noteKey = (qid) => `${NOTE_PREFIX}${qid}`;
const YESNO_OPTIONS = [['yes', 'Yes'], ['no', 'No'], ['na', 'N/A']];

const ChecklistFiller = ({ checklist, jobId, orgId, currentUserId, editable, highlightMissing, isOffice, canRestore }) => { // isOffice / canRestore: removed media list (chklsthide-4f8b2d61)
    const answers = checklist.answers || {};

    const [drafts, setDrafts] = useState({});          // { qid: string } typed, not yet shown from server
    const [numberErrors, setNumberErrors] = useState({});
    const [presence, setPresence] = useState({});      // { qid: bool } photo/signature has media
    const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
    const [saveError, setSaveError] = useState('');

    const pendingRef = useRef({});      // typed answers waiting for the debounce
    const timerRef = useRef(null);
    const queueRef = useRef(Promise.resolve());
    const inFlightRef = useRef(0);
    const failedRef = useRef({});       // answers whose save failed, for "Try again"
    const focusedRef = useRef(null);
    const mountedRef = useRef(true);
    const inFlightKeysRef = useRef({}); // { qid: count }
    const pendingNotesRef = useRef({}); // chklstmulti-3e9b7c15 — typed notes waiting for the debounce
    const failedNotesRef = useRef({});  // notes whose save failed, for "Try again"

    const queueSave = useCallback((batch, noteBatch = {}) => { // chklstmulti-3e9b7c15 — notes ride along
        const keys = Object.keys(batch);
        const noteKeys = Object.keys(noteBatch);
        if (!keys.length && !noteKeys.length) return;
        const draftKeys = [...keys, ...noteKeys.map(noteKey)];
        inFlightRef.current += 1;
        draftKeys.forEach(k => { inFlightKeysRef.current[k] = (inFlightKeysRef.current[k] || 0) + 1; });
        if (mountedRef.current) { setSaveState('saving'); setSaveError(''); }
        queueRef.current = queueRef.current.then(async () => {
            let ok = false;
            try {
                await saveChecklistAnswers({ jobId, checklistId: checklist.id, answers: batch, ...(noteKeys.length ? { notes: noteBatch } : {}) });
                keys.forEach(k => { delete failedRef.current[k]; });
                noteKeys.forEach(k => { delete failedNotesRef.current[k]; });
                ok = true;
            } catch (err) {
                failedRef.current = { ...failedRef.current, ...batch };
                failedNotesRef.current = { ...failedNotesRef.current, ...noteBatch };
                if (mountedRef.current) {
                    setSaveState('error');
                    setSaveError(err?.message || 'That answer could not be saved.');
                }
            } finally {
                inFlightRef.current -= 1;
                draftKeys.forEach(k => { inFlightKeysRef.current[k] -= 1; });
                if (mountedRef.current) {
                    // Hand each field back to the live server value once nothing is pending for it.
                    setDrafts(prev => {
                        const next = { ...prev };
                        draftKeys.forEach(k => {
                            const waiting = k.startsWith(NOTE_PREFIX) ? (k.slice(NOTE_PREFIX.length) in pendingNotesRef.current) : (k in pendingRef.current);
                            if (ok && focusedRef.current !== k && !inFlightKeysRef.current[k] && !waiting) delete next[k];
                        });
                        return next;
                    });
                    if (inFlightRef.current === 0 && !Object.keys(failedRef.current).length && !Object.keys(failedNotesRef.current).length) setSaveState('saved');
                }
            }
        });
    }, [jobId, checklist.id]);

    const flush = useCallback(() => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const batch = pendingRef.current;
        const notes = pendingNotesRef.current;
        pendingRef.current = {};
        pendingNotesRef.current = {};
        queueSave(batch, notes);
    }, [queueSave]);

    // Closing the pop-up or the checklist still sends a waiting typed answer (best effort).
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; flush(); };
    }, [flush]);

    const scheduleTyped = (qid, value) => {
        pendingRef.current = { ...pendingRef.current, [qid]: value };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, TEXT_DEBOUNCE_MS);
    };

    const saveNow = (qid, value) => queueSave({ [qid]: value });

    const retryFailed = () => {
        const batch = failedRef.current;
        const notes = failedNotesRef.current;
        failedRef.current = {};
        failedNotesRef.current = {};
        queueSave(batch, notes);
    };

    // chklstmulti-3e9b7c15 — a note under an answer: typed like a text answer, its own draft key.
    const onNoteChange = (q, raw) => {
        setDrafts(prev => ({ ...prev, [noteKey(q.id)]: raw }));
        pendingNotesRef.current = { ...pendingNotesRef.current, [q.id]: raw.trim() === '' ? null : raw };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, TEXT_DEBOUNCE_MS);
    };
    const onNoteFocus = (q) => { focusedRef.current = noteKey(q.id); };
    const onNoteBlur = (q) => {
        const k = noteKey(q.id);
        if (focusedRef.current === k) focusedRef.current = null;
        flush();
        if (!inFlightKeysRef.current[k]) setDrafts(prev => { const { [k]: _d, ...rest } = prev; return rest; });
    };

    const handlePresence = useCallback((qid, has) => {
        setPresence(prev => (prev[qid] === has ? prev : { ...prev, [qid]: has }));
    }, []);

    // ─── typed fields ───────────────────────────────────────────────────────
    const onTextChange = (q, raw) => {
        setDrafts(prev => ({ ...prev, [q.id]: raw }));
        scheduleTyped(q.id, raw.trim() === '' ? null : raw);
    };

    const onNumberChange = (q, raw) => {
        setDrafts(prev => ({ ...prev, [q.id]: raw }));
        const t = raw.trim().replace(',', '.');
        if (t === '') {
            setNumberErrors(prev => ({ ...prev, [q.id]: '' }));
            scheduleTyped(q.id, null);
            return;
        }
        const n = Number(t);
        if (!Number.isFinite(n)) {
            setNumberErrors(prev => ({ ...prev, [q.id]: 'Enter a number.' }));
            // Drop any queued value for this field; nothing invalid is sent.
            const { [q.id]: _drop, ...rest } = pendingRef.current;
            pendingRef.current = rest;
            return;
        }
        setNumberErrors(prev => ({ ...prev, [q.id]: '' }));
        scheduleTyped(q.id, n);
    };

    const onFocus = (q) => { focusedRef.current = q.id; };
    const onBlur = (q) => {
        if (focusedRef.current === q.id) focusedRef.current = null;
        flush();
        // Nothing waiting for this field? Show the server's value again.
        if (!inFlightKeysRef.current[q.id]) {
            setDrafts(prev => { const { [q.id]: _d, ...rest } = prev; return rest; });
        }
    };

    // ─── render ─────────────────────────────────────────────────────────────
    const inputCls = 'w-full text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30 disabled:bg-slate-50 disabled:text-slate-600';
    const pill = (selected) => `px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
        selected ? 'bg-[#5F4EC9] border-[#5F4EC9] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-[#5F4EC9]/50'
    } disabled:cursor-default`;

    const renderInput = (q) => {
        const value = answers[q.id];
        switch (q.type) {
            case 'short':
                return (
                    <input type="text" maxLength={500} disabled={!editable} className={inputCls}
                        value={q.id in drafts ? drafts[q.id] : (value ?? '')}
                        onFocus={() => onFocus(q)} onBlur={() => onBlur(q)}
                        onChange={e => onTextChange(q, e.target.value)} />
                );
            case 'long':
                return (
                    <textarea rows={3} maxLength={5000} disabled={!editable} className={`${inputCls} resize-y`}
                        value={q.id in drafts ? drafts[q.id] : (value ?? '')}
                        onFocus={() => onFocus(q)} onBlur={() => onBlur(q)}
                        onChange={e => onTextChange(q, e.target.value)} />
                );
            case 'number':
                return (
                    <div>
                        <div className="flex items-center gap-2">
                            <input type="text" inputMode="decimal" disabled={!editable} className={`${inputCls} max-w-[10rem]`}
                                value={q.id in drafts ? drafts[q.id] : (typeof value === 'number' ? String(value) : '')}
                                onFocus={() => onFocus(q)} onBlur={() => onBlur(q)}
                                onChange={e => onNumberChange(q, e.target.value)} />
                            {q.unit && <span className="text-sm text-slate-500">{q.unit}</span>}
                        </div>
                        {numberErrors[q.id] && <p className="text-xs text-red-600 mt-1">{numberErrors[q.id]}</p>}
                    </div>
                );
            case 'date':
                return (
                    <input type="date" disabled={!editable} className={`${inputCls} max-w-[12rem]`}
                        value={typeof value === 'string' ? value : ''}
                        onChange={e => saveNow(q.id, e.target.value || null)} />
                );
            case 'yesno':
                return (
                    <div className="flex gap-2 flex-wrap">
                        {YESNO_OPTIONS.map(([v, label]) => (
                            <button key={v} type="button" disabled={!editable} className={pill(value === v)}
                                onClick={() => saveNow(q.id, value === v ? null : v)}>
                                {label}
                            </button>
                        ))}
                    </div>
                );
            case 'choice':
                return (
                    <div className="flex gap-2 flex-wrap">
                        {(q.options || []).map(opt => (
                            <button key={opt} type="button" disabled={!editable} className={pill(value === opt)}
                                onClick={() => saveNow(q.id, value === opt ? null : opt)}>
                                {opt}
                            </button>
                        ))}
                    </div>
                );
            case 'multi': { // chklstmulti-3e9b7c15 — tick as many as apply; saved in the checklist's option order
                const picked = q.id in drafts ? drafts[q.id] : (Array.isArray(value) ? value : []);
                const toggle = (opt) => {
                    const next = picked.includes(opt) ? picked.filter(o => o !== opt) : [...picked, opt];
                    // Kept locally until saved, so a quick second tap builds on the first (saves run in order).
                    setDrafts(prev => ({ ...prev, [q.id]: next }));
                    saveNow(q.id, next.length ? (q.options || []).filter(o => next.includes(o)) : null);
                };
                return (
                    <div className="flex gap-2 flex-wrap">
                        {(q.options || []).map(opt => (
                            <button key={opt} type="button" disabled={!editable} className={pill(picked.includes(opt))}
                                aria-pressed={picked.includes(opt)} onClick={() => toggle(opt)}>
                                {picked.includes(opt) && <Check size={12} className="inline -mt-0.5 mr-1" />}{opt}
                            </button>
                        ))}
                    </div>
                );
            }
            case 'checkbox':
                return (
                    <label className={`inline-flex items-center gap-2 text-sm text-slate-700 ${editable ? 'cursor-pointer' : ''}`}>
                        <input type="checkbox" disabled={!editable} checked={value === true}
                            onChange={e => saveNow(q.id, e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-[#5F4EC9] focus:ring-[#5F4EC9]/40" />
                        {value === true ? 'Done' : 'Tick when done'}
                    </label>
                );
            case 'photos':
            case 'signature':
                return (
                    <ChecklistMediaQuestion
                        orgId={orgId} jobId={jobId} checklistId={checklist.id} question={q}
                        answer={value} editable={editable} currentUserId={currentUserId} isOffice={isOffice} canRestore={canRestore}
                        onSaveAnswer={saveNow} onPresence={handlePresence}
                    />
                );
            default:
                return null;
        }
    };

    // chklstmulti-3e9b7c15 — the note box (allowNote). Read-only: the saved note, or nothing.
    const renderNote = (q) => {
        const saved = (checklist.answerNotes || {})[q.id];
        const k = noteKey(q.id);
        if (!editable) return saved ? <p className="mt-1.5 text-xs text-slate-600 whitespace-pre-line"><span className="font-semibold">Note:</span> {saved}</p> : null;
        return (
            <textarea rows={2} maxLength={ANSWER_NOTE_MAX} placeholder="Add a note (optional)"
                className={`${inputCls} mt-1.5 text-xs resize-y`}
                value={k in drafts ? drafts[k] : (saved ?? '')}
                onFocus={() => onNoteFocus(q)} onBlur={() => onNoteBlur(q)}
                onChange={e => onNoteChange(q, e.target.value)} />
        );
    };

    const renderQuestion = (q) => {
        if (q.type === 'info') {
            return <p key={q.id} className="text-sm text-slate-600 whitespace-pre-line">{q.label}</p>;
        }
        const answered = isChecklistAnswered(q, answers[q.id], presence[q.id]);
        const missing = highlightMissing && q.required && !answered;
        return (
            <div key={q.id} className={`rounded-lg p-2.5 ${missing ? 'border-2 border-red-400 bg-red-50/40' : 'border border-transparent'}`}>
                <p className="text-sm font-medium text-slate-700 mb-1.5">{q.label}</p>
                {renderInput(q)}
                {takesAttachedPhotos(q) && ( /* chklstmulti-3e9b7c15 — photos on this answer; never count */
                    <div className="mt-2">
                        <ChecklistMediaQuestion attached
                            orgId={orgId} jobId={jobId} checklistId={checklist.id} question={q}
                            answer={null} editable={editable} currentUserId={currentUserId} isOffice={isOffice} canRestore={canRestore} />
                    </div>
                )}
                {q.allowNote === true && renderNote(q)}
                {q.required && (
                    <p className={`text-[10px] mt-1 font-semibold ${missing ? 'text-red-600' : 'text-slate-400'}`}>
                        {missing ? 'Required — not answered yet' : 'Required'}
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-3">
            {(checklist.sections || []).map(section => (
                <div key={section.id} className="space-y-1">
                    {section.title && (
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{section.title}</p>
                    )}
                    {(section.questions || []).map(renderQuestion)}
                </div>
            ))}
            {editable && saveState !== 'idle' && (
                <div className="flex items-center gap-2 text-xs">
                    {saveState === 'saving' && <span className="text-slate-500">Saving…</span>}
                    {saveState === 'saved' && <span className="text-emerald-600 flex items-center gap-1"><Check size={12} /> Saved</span>}
                    {saveState === 'error' && (
                        <span className="text-red-600 flex items-center gap-1.5 flex-wrap">
                            <AlertTriangle size={12} /> {saveError}
                            <button type="button" onClick={retryFailed} className="font-semibold underline">Try again</button>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChecklistFiller;
